// screens/business/BusinessSignUpScreen.js
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';

import { useBusiness } from '../../../context/BusinessContext';
import { storage } from '../../../utils/storage';

const API_BASE_URL = 'https://api.jobeng.ai/api';
const DATA_API_URL = `${API_BASE_URL}/data`; // Dynamic data endpoint
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

export default function BusinessSignupScreen({ navigation }) {
  // Use Business Context for all data management
  const { 
    saveUser, 
    saveCompanyProfile, 
    addNotification,
    isOnline,
    apiAvailable,
    syncAllData
  } = useBusiness();

  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    businessEmail: '',
    industry: '',
    companySize: '',
    password: '',
    confirmPassword: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [serverStatus, setServerStatus] = useState('unknown');
  const [formErrors, setFormErrors] = useState({});
  const [isFocused, setIsFocused] = useState({
    fullName: false,
    companyName: false,
    businessEmail: false,
    industry: false,
    companySize: false,
    password: false,
    confirmPassword: false,
  });

  // Test server connection on mount
  useEffect(() => {
    testServerConnection();
  }, []);

  const testServerConnection = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);
      
      // Test the root endpoint instead of /health
      const response = await fetch(`${API_BASE_URL}/`, {
        method: 'GET',
        headers: { 
          'X-API-Key': API_KEY,
          'Accept': 'application/json'
        },
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      setServerStatus(response.ok ? 'connected' : 'error');
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Server connected:', data.service);
      }
    } catch (error) {
      console.error('Server connection test failed:', error);
      setServerStatus('error');
    }
  };

  // Industry suggestions
  const industrySuggestions = [
    'Civil Engineering',
    'Software Development',
    'Mechanical Engineering',
    'Electrical Engineering',
    'Construction',
    'Architecture',
    'Manufacturing',
    'Consulting',
    'Technology',
    'Healthcare',
    'Finance',
    'Education',
    'Other'
  ];

  // Company size options
  const companySizeOptions = [
    '1-10 employees',
    '11-50 employees',
    '51-200 employees',
    '201-500 employees',
    '500+ employees'
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (field, value) => {
    let error = '';

    switch (field) {
      case 'fullName':
        if (!value || value.trim().length < 2) {
          error = 'Full name must be at least 2 characters long';
        }
        break;
      case 'companyName':
        if (!value || value.trim().length < 2) {
          error = 'Company name must be at least 2 characters long';
        }
        break;
      case 'businessEmail':
        if (!value) {
          error = 'Email is required';
        } else if (!validateEmail(value)) {
          error = 'Please enter a valid email address';
        }
        break;
      case 'industry':
        if (!value || value.trim().length === 0) {
          error = 'Please select an industry';
        }
        break;
      case 'companySize':
        if (!value || value.trim().length === 0) {
          error = 'Please select company size';
        }
        break;
      case 'password':
        if (!value) {
          error = 'Password is required';
        } else if (value.length < 6) {
          error = 'Password must be at least 6 characters long';
        }
        break;
      case 'confirmPassword':
        if (value !== formData.password) {
          error = 'Passwords do not match';
        }
        break;
      default:
        break;
    }

    setFormErrors(prev => ({
      ...prev,
      [field]: error
    }));

    return !error;
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Validate all fields
    Object.keys(formData).forEach(field => {
      const fieldValid = validateField(field, formData[field]);
      if (!fieldValid) {
        isValid = false;
      }
    });

    // Terms agreement validation
    if (!agreeToTerms) {
      errors.terms = 'Please agree to the Terms of Service and Privacy Policy';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFocus = (field) => {
    setIsFocused(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const handleBlur = (field) => {
    setIsFocused(prev => ({
      ...prev,
      [field]: false
    }));
    validateField(field, formData[field]);
  };

  const generateCompanyCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // FIXED: Save user to database using correct dynamic data endpoint
  const saveUserToDatabase = async (userData, token) => {
    try {
      // Remove id field to let database auto-generate
      const { id, ...userDataWithoutId } = userData;
      
      const response = await fetch(`${DATA_API_URL}/business_users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userDataWithoutId)
      });

      const result = await response.json();
      if (!response.ok) {
        console.warn('Failed to save user to database:', result.error);
        return null;
      }
      return result.data || result;
    } catch (error) {
      console.error('Error saving user to database:', error);
      return null;
    }
  };

  // FIXED: Save company to database using correct dynamic data endpoint
  const saveCompanyToDatabase = async (companyData, token) => {
    try {
      // Remove id field to let database auto-generate
      const { id, ...companyDataWithoutId } = companyData;
      
      const response = await fetch(`${DATA_API_URL}/company_profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(companyDataWithoutId)
      });

      const result = await response.json();
      if (!response.ok) {
        console.warn('Failed to save company to database:', result.error);
        return null;
      }
      return result.data || result;
    } catch (error) {
      console.error('Error saving company to database:', error);
      return null;
    }
  };

  // FIXED: Check if email already exists
  const checkEmailExists = async (email) => {
    try {
      const response = await fetch(
        `${DATA_API_URL}/business_users?email=${encodeURIComponent(email)}`,
        {
          headers: {
            'X-API-Key': API_KEY,
            'Accept': 'application/json'
          }
        }
      );
      
      const result = await response.json();
      return result.success && result.data && result.data.length > 0;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  };

  const handleSignup = async () => {
    if (!validateForm()) {
      Alert.alert('Validation Error', 'Please fix the errors in the form');
      return;
    }

    if (serverStatus === 'error') {
      Alert.alert(
        'Connection Warning',
        'Server connection issues detected. Signup may fail. Continue anyway?',
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Continue', onPress: proceedWithSignup }
        ]
      );
    } else {
      proceedWithSignup();
    }
  };

  const proceedWithSignup = async () => {
    setIsLoading(true);
    
    try {
      // First check if email already exists
      const emailExists = await checkEmailExists(formData.businessEmail);
      if (emailExists) {
        Alert.alert(
          'Account Exists',
          'An account with this email already exists. Please sign in instead.',
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Sign In', onPress: () => navigation.navigate('BusinessLogin') }
          ]
        );
        setIsLoading(false);
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      // Step 1: Register user with auth endpoint
      const registerResponse = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY 
        },
        body: JSON.stringify({
          email: formData.businessEmail,
          password: formData.password,
          full_name: formData.fullName,
          user_type: 'business',
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!registerResponse.ok) {
        const errorText = await registerResponse.text();
        let errorMessage = `Registration failed: ${registerResponse.status}`;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const registerResult = await registerResponse.json();

      if (registerResult.success) {
        const token = registerResult.data?.token;
        const userId = registerResult.data?.user?.id;

        // Create user object WITHOUT id (let DB generate)
        const userData = {
          full_name: formData.fullName,
          email: formData.businessEmail,
          phone: '',
          date_of_birth: null,
          address: '',
          emergency_contact_name: '',
          emergency_contact_phone: '',
          profile_image: null,
          job_title: 'Business Owner',
          department: 'Management',
          employee_id: `EMP${Date.now().toString().slice(-6)}`,
          start_date: new Date().toISOString().split('T')[0],
          manager: '',
          skills: [],
          certifications: [],
          experience_years: 0,
          company_name: formData.companyName,
          office_location: '',
          team: 'Management',
          role: 'admin',
          access_level: 'admin',
          status: 'active',
          company_id: 1,
          preferences: {
            notifications: {
              email: true,
              push: true,
              sms: false,
              marketing: false,
              security_alerts: true,
              job_updates: true,
              team_updates: true
            },
            privacy: {
              profile_visibility: 'team',
              contact_visibility: 'managers',
              activity_visibility: 'private',
              search_visibility: true,
              data_sharing: false
            },
            security: {
              two_factor_enabled: false,
              biometric_enabled: false,
              session_timeout: 30,
              login_alerts: true,
              device_tracking: true
            }
          }
        };

        // Create company profile WITHOUT id (let DB generate)
        const companyData = {
          company_name: formData.companyName,
          email: formData.businessEmail,
          industry: formData.industry,
          employee_count: parseInt(formData.companySize?.split('-')[0]) || 1,
          phone: '',
          website: '',
          logo: null,
          address: '',
          description: '',
          vat_number: '',
          registration_number: '',
          legal_name: formData.companyName,
          company_id: 1
        };

        let savedUser = null;
        let savedCompany = null;

        // Step 2: Save user to database
        if (token) {
          savedUser = await saveUserToDatabase(userData, token);
          savedCompany = await saveCompanyToDatabase(companyData, token);
        }

        // Save to context (local storage)
        await saveUser(savedUser || userData);
        await saveCompanyProfile(savedCompany || companyData);

        // Add welcome notification
        await addNotification({
          title: 'Welcome to JobEng!',
          message: `Hello ${formData.fullName}, your business account has been created successfully.`,
          type: 'welcome',
          priority: 'high'
        });

        // Store auth data - USING STORAGE HELPER
        if (token) {
          await storage.setItem('auth_token', token);
        }
        
        // Store the actual database IDs if available
        const finalUserId = savedUser?.id || userId || Date.now();
        const finalCompanyId = savedCompany?.id || finalUserId;
        
        await storage.setItem('user_id', String(finalUserId));
        await storage.setItem('company_id', String(finalCompanyId));
        await storage.setObject('user_data', savedUser || userData);

        // Store signup data for company profile
        await storage.setObject('business_signup_data', {
          companyName: formData.companyName,
          businessEmail: formData.businessEmail,
          industry: formData.industry,
          companySize: formData.companySize,
          fullName: formData.fullName,
          createdAt: new Date().toISOString()
        });

        // Trigger sync if online
        if (isOnline && apiAvailable) {
          await syncAllData();
        }

        Alert.alert(
          'Success!',
          `Welcome ${formData.fullName}! Your business account has been created successfully.`,
          [
            {
              text: 'Continue to Company Profile',
              onPress: () => {
                navigation.navigate('CompanyProfileScreen', {
                  companyProfile: savedCompany || companyData
                });
              }
            }
          ]
        );

      } else {
        Alert.alert('Registration Failed', registerResult.error || 'Unable to create account');
      }

    } catch (error) {
      console.error('Registration error:', error);
      
      let errorMessage = 'Registration failed. ';
      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. Please check your internet connection.';
      } else if (error.message.includes('Network')) {
        errorMessage = 'Network error. Please check your internet connection.';
      } else if (error.message.includes('already exists')) {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
        Alert.alert(
          'Account Exists',
          errorMessage,
          [
            { text: 'Cancel', style: 'cancel' },
            { text: 'Sign In', onPress: () => navigation.navigate('BusinessLogin') }
          ]
        );
        return;
      } else {
        errorMessage += error.message;
      }
      
      Alert.alert('Error', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMultiUserSignup = () => {
    navigation.navigate('MultiUserBusinessSignup');
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContainer}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.container}>
            {/* Header with Logo */}
            <LinearGradient
              colors={['#0e4e96', '#2670ba']}
              style={styles.headerGradient}
            >
              <View style={styles.headerContainer}>
                <Image
                  source={require('../../../../assets/jobeng_full_logo.png')}
                  style={styles.logo}
                  resizeMode="contain"
                />
                <Text style={styles.subtitle}>UK Engineering Excellence</Text>
                <Text style={styles.headerDescription}>Create Your Business Account</Text>
                
                {/* Connection Status Indicator */}
                <View style={styles.connectionStatus}>
                  <Ionicons
                    name={serverStatus === 'connected' ? "cloud-done" : serverStatus === 'error' ? "cloud-offline" : "cloud-outline"}
                    size={16}
                    color={serverStatus === 'connected' ? "#4CAF50" : serverStatus === 'error' ? "#F44336" : "#FFC107"}
                  />
                  <Text style={styles.connectionStatusText}>
                    {serverStatus === 'connected' ? 'Connected' : 
                     serverStatus === 'error' ? 'Connection Error' : 'Checking...'}
                  </Text>
                </View>

                {/* Test Connection Button */}
                <TouchableOpacity
                  style={styles.testConnectionButton}
                  onPress={testServerConnection}
                >
                  <Text style={styles.testConnectionText}>Test Server Connection</Text>
                </TouchableOpacity>
              </View>
            </LinearGradient>

            {/* Signup Form */}
            <View style={styles.formContainer}>
              <Text style={styles.title}>Create Business Account</Text>
              <Text style={styles.description}>
                Join our platform to find top engineering talent
              </Text>

              {/* Multi-User Option */}
              <TouchableOpacity
                style={styles.multiUserOption}
                onPress={handleMultiUserSignup}
              >
                <View style={styles.multiUserIcon}>
                  <Ionicons name="people" size={20} color="#0e4e96" />
                </View>
                <View style={styles.multiUserContent}>
                  <Text style={styles.multiUserTitle}>Need multiple users?</Text>
                  <Text style={styles.multiUserDescription}>
                    Create a multi-user business account for your team
                  </Text>
                </View>
                <Ionicons name="chevron-forward" size={20} color="#64748B" />
              </TouchableOpacity>

              {/* Full Name Input */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Full Name *</Text>
                <View
                  style={[
                    styles.inputWrapper,
                    isFocused.fullName && styles.inputWrapperFocused,
                    formErrors.fullName && styles.inputWrapperError,
                  ]}
                >
                  <Ionicons
                    name="person-outline"
                    size={20}
                    color={
                      formErrors.fullName
                        ? '#F44336'
                        : isFocused.fullName
                        ? '#0e4e96'
                        : '#64748B'
                    }
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Enter your full name"
                    value={formData.fullName}
                    onChangeText={(value) => handleInputChange('fullName', value)}
                    onFocus={() => handleFocus('fullName')}
                    onBlur={() => handleBlur('fullName')}
                    placeholderTextColor="#94A3B8"
                    autoCorrect={false}
                  />
                </View>
                {formErrors.fullName && <Text style={styles.errorText}>{formErrors.fullName}</Text>}
              </View>

              {/* Company Name Input */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Company Name *</Text>
                <View
                  style={[
                    styles.inputWrapper,
                    isFocused.companyName && styles.inputWrapperFocused,
                    formErrors.companyName && styles.inputWrapperError,
                  ]}
                >
                  <Ionicons
                    name="business-outline"
                    size={20}
                    color={
                      formErrors.companyName
                        ? '#F44336'
                        : isFocused.companyName
                        ? '#0e4e96'
                        : '#64748B'
                    }
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Enter your company name"
                    value={formData.companyName}
                    onChangeText={(value) => handleInputChange('companyName', value)}
                    onFocus={() => handleFocus('companyName')}
                    onBlur={() => handleBlur('companyName')}
                    placeholderTextColor="#94A3B8"
                    autoCorrect={false}
                  />
                </View>
                {formErrors.companyName && <Text style={styles.errorText}>{formErrors.companyName}</Text>}
              </View>

              {/* Business Email Input */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Business Email *</Text>
                <View
                  style={[
                    styles.inputWrapper,
                    isFocused.businessEmail && styles.inputWrapperFocused,
                    formErrors.businessEmail && styles.inputWrapperError,
                  ]}
                >
                  <Ionicons
                    name="mail-outline"
                    size={20}
                    color={
                      formErrors.businessEmail
                        ? '#F44336'
                        : isFocused.businessEmail
                        ? '#0e4e96'
                        : '#64748B'
                    }
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Enter your business email"
                    value={formData.businessEmail}
                    onChangeText={(value) => handleInputChange('businessEmail', value)}
                    keyboardType="email-address"
                    onFocus={() => handleFocus('businessEmail')}
                    onBlur={() => handleBlur('businessEmail')}
                    placeholderTextColor="#94A3B8"
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                </View>
                {formErrors.businessEmail && <Text style={styles.errorText}>{formErrors.businessEmail}</Text>}
              </View>

              {/* Industry Input */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Industry *</Text>
                <View
                  style={[
                    styles.inputWrapper,
                    isFocused.industry && styles.inputWrapperFocused,
                    formErrors.industry && styles.inputWrapperError,
                  ]}
                >
                  <Ionicons
                    name="construct-outline"
                    size={20}
                    color={
                      formErrors.industry
                        ? '#F44336'
                        : isFocused.industry
                        ? '#0e4e96'
                        : '#64748B'
                    }
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="e.g., Civil Engineering, Software Development"
                    value={formData.industry}
                    onChangeText={(value) => handleInputChange('industry', value)}
                    onFocus={() => handleFocus('industry')}
                    onBlur={() => handleBlur('industry')}
                    placeholderTextColor="#94A3B8"
                    autoCorrect={false}
                  />
                </View>
                {formErrors.industry && <Text style={styles.errorText}>{formErrors.industry}</Text>}
                {/* Industry Suggestions */}
                {isFocused.industry && (
                  <ScrollView
                    horizontal
                    showsHorizontalScrollIndicator={false}
                    style={styles.suggestionsContainer}
                  >
                    {industrySuggestions.map((suggestion, index) => (
                      <TouchableOpacity
                        key={index}
                        style={styles.suggestionChip}
                        onPress={() => {
                          handleInputChange('industry', suggestion);
                          handleBlur('industry');
                        }}
                      >
                        <Text style={styles.suggestionText}>{suggestion}</Text>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                )}
              </View>

              {/* Company Size Input */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Company Size *</Text>
                <View
                  style={[
                    styles.inputWrapper,
                    isFocused.companySize && styles.inputWrapperFocused,
                    formErrors.companySize && styles.inputWrapperError,
                  ]}
                >
                  <Ionicons
                    name="people-outline"
                    size={20}
                    color={
                      formErrors.companySize
                        ? '#F44336'
                        : isFocused.companySize
                        ? '#0e4e96'
                        : '#64748B'
                    }
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Select company size"
                    value={formData.companySize}
                    onChangeText={(value) => handleInputChange('companySize', value)}
                    onFocus={() => handleFocus('companySize')}
                    onBlur={() => handleBlur('companySize')}
                    placeholderTextColor="#94A3B8"
                    autoCorrect={false}
                  />
                </View>
                {formErrors.companySize && <Text style={styles.errorText}>{formErrors.companySize}</Text>}
                {/* Company Size Options */}
                {isFocused.companySize && (
                  <View style={styles.optionsContainer}>
                    {companySizeOptions.map((option, index) => (
                      <TouchableOpacity
                        key={index}
                        style={styles.optionItem}
                        onPress={() => {
                          handleInputChange('companySize', option);
                          handleBlur('companySize');
                        }}
                      >
                        <Text style={styles.optionText}>{option}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                )}
              </View>

              {/* Password Input */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Password *</Text>
                <View
                  style={[
                    styles.inputWrapper,
                    isFocused.password && styles.inputWrapperFocused,
                    formErrors.password && styles.inputWrapperError,
                  ]}
                >
                  <Ionicons
                    name="lock-closed-outline"
                    size={20}
                    color={
                      formErrors.password
                        ? '#F44336'
                        : isFocused.password
                        ? '#0e4e96'
                        : '#64748B'
                    }
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Enter password (min 6 characters)"
                    value={formData.password}
                    onChangeText={(value) => handleInputChange('password', value)}
                    secureTextEntry={!showPassword}
                    onFocus={() => handleFocus('password')}
                    onBlur={() => handleBlur('password')}
                    placeholderTextColor="#94A3B8"
                    autoCorrect={false}
                  />
                  <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                    <Ionicons
                      name={showPassword ? "eye-off" : "eye"}
                      size={20}
                      color="#64748B"
                    />
                  </TouchableOpacity>
                </View>
                {formErrors.password && <Text style={styles.errorText}>{formErrors.password}</Text>}
              </View>

              {/* Confirm Password Input */}
              <View style={styles.inputContainer}>
                <Text style={styles.inputLabel}>Confirm Password *</Text>
                <View
                  style={[
                    styles.inputWrapper,
                    isFocused.confirmPassword && styles.inputWrapperFocused,
                    (formErrors.confirmPassword || (formData.confirmPassword && formData.password !== formData.confirmPassword)) && styles.inputWrapperError,
                  ]}
                >
                  <Ionicons
                    name="lock-closed-outline"
                    size={20}
                    color={
                      formErrors.confirmPassword
                        ? '#F44336'
                        : isFocused.confirmPassword
                        ? '#0e4e96'
                        : '#64748B'
                    }
                  />
                  <TextInput
                    style={styles.input}
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChangeText={(value) => handleInputChange('confirmPassword', value)}
                    secureTextEntry={!showConfirmPassword}
                    onFocus={() => handleFocus('confirmPassword')}
                    onBlur={() => handleBlur('confirmPassword')}
                    placeholderTextColor="#94A3B8"
                    autoCorrect={false}
                  />
                  <TouchableOpacity onPress={() => setShowConfirmPassword(!showConfirmPassword)}>
                    <Ionicons
                      name={showConfirmPassword ? "eye-off" : "eye"}
                      size={20}
                      color="#64748B"
                    />
                  </TouchableOpacity>
                </View>
                {formErrors.confirmPassword && <Text style={styles.errorText}>{formErrors.confirmPassword}</Text>}
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <Text style={styles.errorText}>Passwords do not match</Text>
                )}
              </View>

              {/* Password Requirements */}
              <View style={styles.passwordRequirements}>
                <Text style={styles.passwordRequirementsTitle}>Password must contain:</Text>
                <View style={styles.passwordRequirement}>
                  <Ionicons 
                    name={formData.password.length >= 6 ? "checkmark-circle" : "ellipse-outline"} 
                    size={16} 
                    color={formData.password.length >= 6 ? "#10B981" : "#64748B"} 
                  />
                  <Text style={styles.passwordRequirementText}>At least 6 characters</Text>
                </View>
                <View style={styles.passwordRequirement}>
                  <Ionicons 
                    name={/[A-Z]/.test(formData.password) ? "checkmark-circle" : "ellipse-outline"} 
                    size={16} 
                    color={/[A-Z]/.test(formData.password) ? "#10B981" : "#64748B"} 
                  />
                  <Text style={styles.passwordRequirementText}>At least one uppercase letter</Text>
                </View>
                <View style={styles.passwordRequirement}>
                  <Ionicons 
                    name={/[a-z]/.test(formData.password) ? "checkmark-circle" : "ellipse-outline"} 
                    size={16} 
                    color={/[a-z]/.test(formData.password) ? "#10B981" : "#64748B"} 
                  />
                  <Text style={styles.passwordRequirementText}>At least one lowercase letter</Text>
                </View>
                <View style={styles.passwordRequirement}>
                  <Ionicons 
                    name={/[0-9]/.test(formData.password) ? "checkmark-circle" : "ellipse-outline"} 
                    size={16} 
                    color={/[0-9]/.test(formData.password) ? "#10B981" : "#64748B"} 
                  />
                  <Text style={styles.passwordRequirementText}>At least one number</Text>
                </View>
              </View>

              {/* Terms and Conditions */}
              <TouchableOpacity
                style={styles.termsContainer}
                onPress={() => setAgreeToTerms(!agreeToTerms)}
              >
                <View style={[styles.checkbox, agreeToTerms && styles.checkedCheckbox]}>
                  {agreeToTerms && <Ionicons name="checkmark" size={14} color="#fff" />}
                </View>
                <Text style={styles.termsText}>
                  I agree to the{' '}
                  <Text style={styles.termsLink}>Terms of Service</Text>
                  {' '}and{' '}
                  <Text style={styles.termsLink}>Privacy Policy</Text>
                </Text>
              </TouchableOpacity>
              {formErrors.terms && <Text style={styles.errorText}>{formErrors.terms}</Text>}

              {/* Signup Button */}
              <TouchableOpacity
                style={[styles.signupButton, isLoading && styles.signupButtonDisabled]}
                onPress={handleSignup}
                disabled={isLoading}
              >
                {isLoading ? (
                  <ActivityIndicator size={20} color="#FFFFFF" />
                ) : (
                  <>
                    <Text style={styles.signupButtonText}>Create Account</Text>
                    <Ionicons name="arrow-forward" size={20} color="#fff" />
                  </>
                )}
              </TouchableOpacity>

              {/* Login Link */}
              <View style={styles.loginContainer}>
                <Text style={styles.loginText}>Already have a business account? </Text>
                <TouchableOpacity onPress={() => navigation.navigate('BusinessLogin')}>
                  <Text style={styles.loginLink}>Sign in</Text>
                </TouchableOpacity>
              </View>

              {/* Other Signup Types */}
              <View style={styles.switchContainer}>
                <TouchableOpacity onPress={() => navigation.navigate('ProfessionalSignup')}>
                  <Text style={styles.switchText}>Professional Signup</Text>
                </TouchableOpacity>
                <Text style={styles.switchDivider}> • </Text>
                <TouchableOpacity onPress={() => navigation.navigate('LearningSignup')}>
                  <Text style={styles.switchText}>Learning Signup</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
  },
  headerGradient: {
    paddingTop: 20,
    paddingBottom: 30,
    paddingHorizontal: 24,
  },
  headerContainer: {
    alignItems: 'center',
  },
  logo: {
    width: 100,
    height: 100,
    marginBottom: 12,
    tintColor: '#fff',
  },
  subtitle: {
    fontSize: 16,
    color: '#fff',
    fontWeight: '500',
    marginBottom: 4,
  },
  headerDescription: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    textAlign: 'center',
    marginBottom: 10,
  },
  connectionStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 5,
  },
  connectionStatusText: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.8)',
    marginLeft: 4,
  },
  testConnectionButton: {
    marginTop: 10,
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 16,
  },
  testConnectionText: {
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.9)',
    fontWeight: '500',
  },
  formContainer: {
    backgroundColor: '#F8FAFC',
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    marginTop: -20,
    padding: 24,
    flex: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
    color: '#1E293B',
    marginBottom: 8,
    textAlign: 'center',
    marginTop: 10,
  },
  description: {
    fontSize: 16,
    color: '#64748B',
    marginBottom: 24,
    textAlign: 'center',
  },
  multiUserOption: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  multiUserIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#EBF4FF',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  multiUserContent: {
    flex: 1,
  },
  multiUserTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 2,
  },
  multiUserDescription: {
    fontSize: 12,
    color: '#64748B',
    lineHeight: 16,
  },
  inputContainer: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 8,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderWidth: 2,
    borderColor: '#E2E8F0',
  },
  inputWrapperFocused: {
    borderColor: '#0e4e96',
    backgroundColor: '#FFFFFF',
  },
  inputWrapperError: {
    borderColor: '#F44336',
    backgroundColor: '#FFEBEE',
  },
  input: {
    flex: 1,
    fontSize: 16,
    color: '#1E293B',
    marginLeft: 12,
    padding: 0,
  },
  errorText: {
    fontSize: 12,
    color: '#F44336',
    marginTop: 4,
    marginLeft: 4,
  },
  suggestionsContainer: {
    marginTop: 8,
  },
  suggestionChip: {
    backgroundColor: '#EBF4FF',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
  },
  suggestionText: {
    fontSize: 12,
    color: '#0e4e96',
    fontWeight: '500',
  },
  optionsContainer: {
    marginTop: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  optionItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  optionText: {
    fontSize: 14,
    color: '#1E293B',
  },
  passwordRequirements: {
    marginBottom: 16,
    padding: 12,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  passwordRequirementsTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1E293B',
    marginBottom: 8,
  },
  passwordRequirement: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  passwordRequirementText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 8,
  },
  termsContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#E2E8F0',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
    marginTop: 2,
  },
  checkedCheckbox: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  termsText: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    flex: 1,
  },
  termsLink: {
    color: '#0e4e96',
    fontWeight: '500',
  },
  signupButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 12,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#0e4e96',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 4,
    marginBottom: 20,
  },
  signupButtonDisabled: {
    backgroundColor: '#94A3B8',
    shadowOpacity: 0,
    elevation: 0,
  },
  signupButtonText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
    marginRight: 8,
  },
  loginContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  loginText: {
    fontSize: 16,
    color: '#64748B',
  },
  loginLink: {
    fontSize: 16,
    color: '#0e4e96',
    fontWeight: '600',
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  switchText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '500',
  },
  switchDivider: {
    fontSize: 14,
    color: '#64748B',
  },
});